/* eslint-disable react/destructuring-assignment */
import { useState } from 'react';
import Select, { ActionMeta, CommonProps, components, InputProps, SelectComponentsConfig } from 'react-select';
import cn from 'classnames';
import { MultiValueGenericProps, MultiValueRemoveProps } from 'react-select/src/components/MultiValue';

import styles from './SelectInput.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IOptionType = any;
type ISelectComponents = SelectComponentsConfig<IOptionType, boolean>;

const Control: ISelectComponents['Control'] = props => {
  const { isDisabled, menuIsOpen } = props.selectProps;
  const controlClassName = cn(styles.Control, {
    [styles.ControlMenuIsOpen]: menuIsOpen,
    [styles.ControlDisabled]: isDisabled,
  });
  return <components.Control className={controlClassName} {...props} />;
};

const ValueContainer: ISelectComponents['ValueContainer'] = props => {
  const { isMulti } = props.selectProps;
  const valueContainerClassName = cn(styles.ValueContainer, {
    [styles.ValueContainerSingle]: !isMulti,
    [styles.ValueContainerMulti]: isMulti,
  });
  return <components.ValueContainer className={valueContainerClassName} {...props} />;
};

const getComponentSingleValue = ({ isMultiLine }: ISelectInputProps) => {
  const SingleValue: ISelectComponents['SingleValue'] = props => {
    const { value, menuIsOpen } = props.selectProps;
    const singleValueClassName = cn(styles.SingleValue, {
      [styles.SingleValueSingleLine]: !isMultiLine && !menuIsOpen,
      [styles.SingleValueInputHidden]: value && !menuIsOpen,
    });
    return <components.SingleValue className={singleValueClassName} {...props} />;
  };
  return SingleValue;
};

const MultiValueContainer = (props: MultiValueGenericProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValue, {
    [styles.MultiValueDisabled]: props.selectProps.isDisabled,
  });
  return <components.MultiValueContainer {...props} />;
};

const MultiValueLabel = (props: MultiValueGenericProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValueLabel);
  return <components.MultiValueLabel {...props} />;
};

const MultiValueRemove = (props: MultiValueRemoveProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValueRemove, {
    [styles.MultiValueRemoveDisabled]: props.selectProps.isDisabled,
  });
  return <components.MultiValueRemove {...props} />;
};

const IndicatorsContainer: ISelectComponents['IndicatorsContainer'] = props => (
  <components.IndicatorsContainer className={styles.Indicators} {...props} />
);

const Input = (props: CommonProps<IOptionType, boolean> & InputProps) => {
  const { menuIsOpen } = props.selectProps;
  const inputClassName = cn(styles.Input, { [styles.InputHidden]: !menuIsOpen });
  return <components.Input className={inputClassName} {...props} />;
};

const CustomOption: ISelectComponents['Option'] = props => {
  const { isDisabled, isFocused, isSelected } = props;
  return (
    <components.Option
      className={cn(styles.Option, {
        [styles.OptionDisabled]: isDisabled,
        [styles.OptionFocused]: isFocused,
        [styles.OptionSelected]: isSelected,
      })}
      {...props}
    />
  );
};

const NoOptionsMessage: ISelectComponents['NoOptionsMessage'] = props => (
  <components.NoOptionsMessage className={styles.NoOptionsMessage} {...props} />
);

export const initialSelectInputData = {
  id: '',
  value: '',
  label: '',
};

export interface ISelectOption<T = string | number> {
  id: T;
  value?: unknown;
  label: string;
  enabled?: boolean | null; // ToDo: rename to "isEnabled"
  disabled?: boolean;
  isDisabled?: boolean;
  byDefault?: boolean;
}

export interface ISelectInputProps {
  options?: ISelectOption[];
  className?: string;
  id?: string;
  name?: string;
  value?: null | ISelectOption | ISelectOption[];
  defaultValue?: null | ISelectOption | ISelectOption[];
  placeholder?: string;
  label?: string;
  size?: 'medium' | 'large';
  noOptionsMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  error?: string;
  isMultiLine?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  opensOnTop?: boolean;
  validateOnSubmit?: boolean;
  isActiveFillInProcessMessage?: boolean;
  defaultOptions?: ISelectOption[] | boolean;
  loadOptions?: (inputValue: string, callback?: (options: ISelectOption[]) => void) => Promise<unknown> | void;
  onChange?: (value: unknown, actionMeta: ActionMeta<ISelectOption>) => void;
  onClear?: () => void;
}

export const SelectInput = ({
  className,
  id,
  placeholder = '',
  options,
  defaultValue = null,
  value = null,
  label,
  size = 'large',
  noOptionsMessage = 'Значение отсутствует',
  disabled,
  invalid,
  error,
  isMultiLine = true,
  isSearchable = false,
  isClearable = true,
  loadOptions,
  opensOnTop = false,
  onClear,
  ...rest
}: ISelectInputProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const onMenuOpen = () => setMenuOpen(true);
  const onMenuClose = () => setMenuOpen(false);
  const classNames = cn([
    styles.SelectInput,
    className,
    {
      [styles.SelectInputSizeMedium]: size === 'medium',
      [styles.SelectInputSizeLarge]: size === 'large',
      [styles.SelectInputNotSearchable]: !isSearchable,
      [styles.SelectInputSearchable]: isSearchable,
      [styles.SelectInputInvalid]: invalid,
      [styles.SelectInputDisabled]: disabled,
      [styles.SelectInputOpensOnTop]: opensOnTop && menuOpen,
    },
  ]);

  const selectContainerClassName = cn([
    styles.SelectContainer,
    'select-field-container',
    {
      'select-field-container--size-medium': size === 'medium',
      'select-field-container--size-large': size === 'large',
    },
  ]);

  // reset default styles
  const customStyles = {
    container: () => ({}),
    control: () => ({}),
    indicatorsContainer: () => ({}),
    menu: () => ({}),
    multiValue: () => ({}),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({}),
    noOptionsMessage: () => ({}),
    option: () => ({}),
    singleValue: () => ({}),
    valueContainer: () => ({}),
  };

  const SingleValue = getComponentSingleValue({ isMultiLine });
  const restyledComponents = {
    Control,
    IndicatorsContainer,
    Input,
    MultiValueContainer,
    MultiValueLabel,
    MultiValueRemove,
    NoOptionsMessage,
    Option: CustomOption,
    SingleValue,
    ValueContainer,
  };
  const getNoOptionMessage = () => noOptionsMessage;

  return (
    <div className={classNames}>
      {label && (
        <label className={styles.Label} htmlFor={id}>
          {label}
        </label>
      )}
      <Select
        className={selectContainerClassName}
        classNamePrefix="select-field"
        components={restyledComponents}
        styles={customStyles}
        id={id}
        noOptionsMessage={getNoOptionMessage}
        maxMenuHeight={200}
        hideSelectedOptions={false}
        options={options}
        defaultValue={defaultValue}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isDisabled={disabled}
        isClearable={onClear ? false : isClearable}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        value={value ?? defaultValue ?? ''}
        {...rest}
      />
      {isClearable && onClear && <button className={styles.ClearButton} type="button" onClick={onClear} hidden />}
      {invalid && error && <div className={styles.SelectInputError}>{error}</div>}
    </div>
  );
};
